import { useQuery } from "@tanstack/react-query";
import { ExperimentRepository } from "../../domain/interfaces/ExperimentRepository";
import {
  DateRange,
  ExperimentResults,
  ExperimentStatus,
  MetricRange,
} from "../../domain/experiment.entity";
import { experimentsQueryKeys } from "../../infrastructure/cache/ExperimentsCacheStrategy";
import { useHotelTimeZone } from "./useHotelTimeZone";
import { InvalidHotelTimeZoneError, resolveMetricRange } from "../../domain/metricRangeResolver";
import { useExperiment } from "./useExperiment";

// BNG-side UX default only — GetExperimentResults has no backend cache to align with
// (see this story's Plan vs. Actual Implementation, point 6). Flagged in Open Questions.
export const RUNNING_RESULTS_POLL_INTERVAL_MS = 60 * 1000;

interface UseExperimentResultsOptions {
  enabled?: boolean;
}

export function useExperimentResults(
  propertyTicker: string,
  id: string | undefined,
  range: MetricRange,
  repository: ExperimentRepository,
  custom?: DateRange,
  { enabled = true }: UseExperimentResultsOptions = {}
) {
  const hotelTimeZone = useHotelTimeZone();
  // Shares the shell's own `useExperiment` query (same query key, US-003) — no extra network call
  // in the common case where the shell/sibling sections already fetched this experiment. Needed
  // here because resolving `range`/`custom` into a concrete `{from, to}` (US-005) requires the
  // experiment's own `startedAt`.
  const { data: experiment } = useExperiment(propertyTicker, id, repository);

  return useQuery({
    queryKey: experimentsQueryKeys.result(propertyTicker, id ?? "", range, custom),
    queryFn: (): Promise<ExperimentResults> => {
      if (!id) throw new Error("Experiment ID is required");
      if (!experiment) throw new Error("Experiment must be loaded before fetching results");
      if (!hotelTimeZone) throw new Error("Hotel time zone must be loaded before fetching results");
      const { from, to } = resolveMetricRange(
        range,
        hotelTimeZone,
        { startedAt: experiment.startedAt, archivedAt: null },
        custom
      );
      return repository.results(id, from, to);
    },
    // Resolved: an explicit `enabled` option (default `true`) is ANDed with the existing guards,
    // so a caller can idle the query outright without needing an `id: undefined` workaround. Also
    // gated on `experiment`/`hotelTimeZone` being loaded — both are required inputs to
    // `resolveMetricRange` (US-005), so the query stays idle rather than firing (and failing)
    // before they're available.
    // An incomplete CUSTOM pick (`range === "CUSTOM"` with no `custom` yet) is guarded here
    // rather than per-caller: `resolveMetricRange` throws on it, so every one of this hook's
    // callers needs the same guard, and only the chart used to carry it.
    enabled: enabled && !!id && !!experiment && !!hotelTimeZone && !(range === "CUSTOM" && !custom),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    // AC1/AC2: poll only while the experiment is RUNNING. Before the first successful fetch,
    // query.state.data is undefined, so no interval is set up until a status is actually known.
    refetchInterval: query =>
      query.state.data?.experiment.status === ExperimentStatus.EXPERIMENT_STATUS_RUNNING
        ? RUNNING_RESULTS_POLL_INTERVAL_MS
        : false,
    retry: (failureCount, error) => {
      // A bad hotel time zone is a deterministic, non-network failure — retrying it is pointless.
      if (error instanceof InvalidHotelTimeZoneError) return false;
      return failureCount < 3;
    },
  });
}
